// @flow

// eslint-disable-next-line no-restricted-imports
import * as React from 'react';
import { createContext, useContext, useState } from 'react';
import i18n from 'i18next';
import { initReactIntl } from '@tectonics/translate';
import { type Language, LANGUAGES_BY_COUNTRY } from '@tectonics/translate/enums/language-by-country.enum';
import { getCurrentLanguage, getDefaultCountry } from '@tectonics/controller/language/language-service';

export type LanguageContextDataProps = {
  changeLanguage: (country: string) => Promise<void>,
  addCountry: (country: string) => void,
  currentLanguage: string,
};

export const LanguageContext = createContext<LanguageContextDataProps>({});

type Props = {
  children: React.Node,
};

export const useLanguage = () => {
  return useContext(LanguageContext);
};

function selectLanguagesByCountry(country: string): Language[] {
  return LANGUAGES_BY_COUNTRY[country] || [];
}

export function LanguageContextProvider({ children }: Props): React.Node {
  const language: string = getCurrentLanguage();
  const [currentLanguage, setCurrentLanguage] = useState<string>(language);

  async function changeLanguage(language: string) {
    if (language !== undefined) {
      sessionStorage.setItem('language', language);
    }
    setCurrentLanguage(language);
    const defaultCountry: string = getDefaultCountry();
    await initReactIntl(defaultCountry);
    await i18n.changeLanguage(language);
  }

  function addCountry(country: string) {
    const selectedLanguages: Language[] = selectLanguagesByCountry(country);
    const [{ code }] = selectedLanguages;

    if (currentLanguage === '') {
      sessionStorage.setItem('language', code);
    }
    sessionStorage.setItem('country', country);
  }

  return (
    <LanguageContext.Provider value={{
      changeLanguage,
      addCountry,
      currentLanguage,
    }}>
      {children}
    </LanguageContext.Provider>
  );
}
